/**
 * API Constants
 * API configuration and endpoint paths
 */

import config from '../config/environment.config';

// Base API URL
export const API_BASE_URL = config.apiBaseUrl;

// API Timeout
export const API_TIMEOUT = config.apiTimeout;

/**
 * Auth Endpoints
 */
export const AUTH_ENDPOINTS = {
  LOGIN: '/api/v1/auth/login',
  ME: '/api/v1/auth/me',
  FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
  // Reset password (first login / temp password) - authenticated
  RESET_PASSWORD: '/api/v1/auth/resetpassword',
  // Alias kept for backwards compatibility in UI code
  FORCE_RESET_PASSWORD: '/api/v1/auth/resetpassword',
  CREATE_CLINIC: '/api/v1/auth/create-clinic',
  CREATE_CLINIC_ADMIN: '/api/v1/auth/create-clinic-admin',
  REFRESH_TOKEN: '/api/v1/auth/refresh-token',
  LOGOUT: '/api/v1/auth/logout',
  // Prefer /me for session verification (verify may not exist in all envs)
  VERIFY_TOKEN: '/api/v1/auth/me',
  VERIFY_EMAIL: '/api/v1/auth/verify-email',
  RESEND_VERIFICATION: '/api/v1/auth/resend-verification',
  CHANGE_PASSWORD: '/api/v1/auth/change-password',
} as const;

/**
 * Clinic Endpoints
 */
export const CLINIC_ENDPOINTS = {
  GET_ME: '/api/v1/clinic/me',
  GET_BY_ID: '/api/v1/clinic/{id}',
  UPDATE_STATUS: '/api/v1/clinic/status',
  // Onboarding Steps
  ONBOARDING_CLINIC_INFO: '/api/v1/clinic/onboarding/clinic-info',
  ONBOARDING_PRIMARY_ADDRESS: '/api/v1/clinic/onboarding/primary-address',
  ONBOARDING_CONTACT_PERSON: '/api/v1/clinic/onboarding/contact-person',
  ONBOARDING_LICENSING: '/api/v1/clinic/onboarding/licensing',
  ONBOARDING_SERVICES_PROVIDED: '/api/v1/clinic/onboarding/services-provided',
  ONBOARDING_OPERATIONS: '/api/v1/clinic/onboarding/operations',
  ONBOARDING_INSURANCE_BILLING: '/api/v1/clinic/onboarding/insurance-billing',
  ONBOARDING_DOCUMENTS_UPLOAD: '/api/v1/clinic/onboarding/documents-upload',
  ONBOARDING_TERMS_COMPLIANCE: '/api/v1/clinic/onboarding/terms-compliance',
  ONBOARDING_REVIEW_SUBMIT: '/api/v1/clinic/onboarding/review-submit',
  // Staff Management
  STAFF_CREATE: '/api/v1/staff',
  STAFF_CREATE_DOCTOR: '/api/v1/staff/doctor',
  STAFF_CREATE_RECEPTIONIST: '/api/v1/staff/receptionist',
  STAFF_GET_BY_ID: '/api/v1/staff/{id}',
  STAFF_UPDATE_STATUS: '/api/v1/staff/{id}/status',
  // Staff Onboarding - Doctor
  STAFF_ONBOARDING_DOCTOR_BASIC_INFO: '/api/v1/staff/onboarding/{id}/doctor/basic-info',
  STAFF_ONBOARDING_DOCTOR_CREDENTIALS: '/api/v1/staff/onboarding/{id}/doctor/credentials',
  STAFF_ONBOARDING_DOCTOR_SERVICES: '/api/v1/staff/onboarding/{id}/doctor/services',
  STAFF_ONBOARDING_DOCTOR_PRICING: '/api/v1/staff/onboarding/{id}/doctor/pricing',
  STAFF_ONBOARDING_DOCTOR_AVAILABILITY: '/api/v1/staff/onboarding/{id}/doctor/availability',
  STAFF_ONBOARDING_DOCTOR_PAYMENT: '/api/v1/staff/onboarding/{id}/doctor/payment',
  STAFF_ONBOARDING_DOCTOR_SUBMIT: '/api/v1/staff/onboarding/{id}/doctor/submit',
  // Staff Onboarding - Receptionist
  STAFF_ONBOARDING_RECEPTION_BASIC_INFO: '/api/v1/staff/onboarding/{id}/reception/basic-info',
  STAFF_ONBOARDING_RECEPTION_CREDENTIALS: '/api/v1/staff/onboarding/{id}/reception/credentials',
  STAFF_ONBOARDING_RECEPTION_SCHEDULE: '/api/v1/staff/onboarding/{id}/reception/schedule',
  STAFF_ONBOARDING_RECEPTION_PERMISSIONS: '/api/v1/staff/onboarding/{id}/reception/permissions',
  STAFF_ONBOARDING_RECEPTION_SUBMIT: '/api/v1/staff/onboarding/{id}/reception/submit',
} as const;

/**
 * Appointment Endpoints
 */
export const APPOINTMENT_ENDPOINTS = {
  CREATE: '/api/v1/appointments',
  LIST: '/api/v1/appointments',
  LIST_BY_STAFF: '/api/v1/appointments/staff/{staffId}',
  GET_BY_ID: '/api/v1/appointments/{id}',
  CALENDAR: '/api/v1/appointments/calendar',
  CALENDAR_BY_STAFF: '/api/v1/appointments/staff/{staffId}/calendar',
  UPDATE_STATUS: '/api/v1/appointments/{id}/status',
  RESCHEDULE: '/api/v1/appointments/{id}/reschedule',
  // Prescription endpoints
  PRESCRIPTION_CREATE_OR_UPDATE: '/api/v1/appointments/{id}/prescription',
  PRESCRIPTION_GET: '/api/v1/appointments/{id}/prescription',
  PRESCRIPTION_DELETE: '/api/v1/appointments/{id}/prescription',
  // Clinical Note endpoints
  CLINICAL_NOTE_CREATE_OR_UPDATE: '/api/v1/appointments/{id}/clinical-note',
  CLINICAL_NOTE_GET: '/api/v1/appointments/{id}/clinical-note',
  CLINICAL_NOTE_DELETE: '/api/v1/appointments/{id}/clinical-note',
} as const;

/**
 * Referral Endpoints
 */
export const REFERRAL_ENDPOINTS = {
  LIST: '/api/v1/referrals',
  LIST_BY_AGENCY: '/api/v1/referrals/agency/{agencyId}',
  GET_BY_ID: '/api/v1/referrals/{id}',
  CREATE: '/api/v1/referrals',
  UPDATE: '/api/v1/referrals/{id}',
  UPDATE_STATUS: '/api/v1/referrals/{id}/status',
  DELETE: '/api/v1/referrals/{id}',
} as const;

/**
 * Service Request Endpoints
 */
export const SERVICE_REQUEST_ENDPOINTS = {
  LIST_BY_AGENCY: '/api/v1/homehealth/request/agency/{agencyId}',
  GET_BY_ID: '/api/v1/homehealth/request/{id}',
  UPDATE_STATUS: '/api/v1/homehealth/request/{id}/status',
  PATCH_DETAILS: '/api/v1/homehealth/request/{id}/details',
  CREATE_PUBLIC: '/api/v1/homehealth/request',
  HEALTH_RECORD_SHARES: '/api/v1/homehealth/request/{serviceRequestId}/health-record-shares',
  SHARE_STATUS: '/api/v1/homehealth/request/{serviceRequestId}/share',
  SHARE_UNLOCK: '/api/v1/homehealth/request/{serviceRequestId}/share/unlock',
  SHARE_SECTION_REQUESTS: '/api/v1/homehealth/request/{serviceRequestId}/share/section-requests',
  MESSAGES: '/api/v1/homehealth/request/{serviceRequestId}/messages',
  MESSAGES_MARK_READ: '/api/v1/homehealth/request/{serviceRequestId}/messages/mark-read',
  MESSAGE_NOTIFICATIONS: '/api/v1/homehealth/request/message-notifications',
} as const;

/**
 * Master Data Endpoints
 */
export const MASTER_DATA_ENDPOINTS = {
  MEDICATIONS: '/api/v1/master-data/medications',
  INSURANCE_CARRIERS: '/api/v1/insurance/carriers',
  INSURANCE_CARRIER_BY_ID: '/api/v1/insurance/carriers/{id}',
  INSURANCE_CARRIER_PLANS: '/api/v1/insurance/carriers/{id}/plans',
} as const;

/**
 * Reviews Endpoints
 */
export const REVIEWS_ENDPOINTS = {
  LIST: '/api/v1/agencies/me/reviews',
  STATS: '/api/v1/agencies/me/reviews/stats',
  RESPONSE: (reviewId: string) => `/api/v1/agencies/me/reviews/${reviewId}/response`,
} as const;

/**
 * Agency Endpoints
 */
/**
 * Registration lookup endpoints (NPPES, CMS CCN, email OTP)
 */
export const REGISTER_LOOKUP_ENDPOINTS = {
  NPPES_CHECK: (npi: string) => `/api/v1/nppes/organizations/${npi}/check`,
  CMS_AGENCY_BY_CCN: (ccn: string) => `/api/v1/cms/agencies/${ccn}`,
  REGISTER_EMAIL_SEND_CODE: '/api/v1/register/email/send-code',
  REGISTER_EMAIL_VERIFY_CODE: '/api/v1/register/email/verify-code',
} as const;

export const AGENCY_ENDPOINTS = {
  REGISTER: '/api/v1/agencies/register',
  SEARCH_UNVERIFIED: '/api/v1/agencies/unverified/search',
  GET_ME: '/api/v1/agencies/me',
  GET_BY_ID: '/api/v1/agencies/{id}',
  HEALTH_RECORD_SHARES: '/api/v1/agencies/{id}/health-record-shares',
  // Onboarding Steps
  STEP_1_AGENCY_INFO: '/api/v1/agencies/{id}/steps/agency-info',
  STEP_2_ADDRESS: '/api/v1/agencies/{id}/steps/address',
  STEP_3_CONTACT: '/api/v1/agencies/{id}/steps/contact',
  STEP_4_LICENSING: '/api/v1/agencies/{id}/steps/licensing',
  STEP_5_SERVICES: '/api/v1/agencies/{id}/steps/services',
  STEP_6_OPERATIONS: '/api/v1/agencies/{id}/steps/operations',
  STEP_7_INSURANCE: '/api/v1/agencies/{id}/steps/insurance',
  STEP_8_DOCUMENTS: '/api/v1/agencies/{id}/steps/documents',
  STEP_9_PAYMENT: '/api/v1/agencies/{id}/steps/payment',
  STEP_10_PROFILE_MARKETING: '/api/v1/agencies/{id}/steps/profile-marketing',
  // Other operations
  COMPLETE_ONBOARDING: '/api/v1/agencies/{id}/complete-onboarding',
  UPLOAD_DOCUMENT: '/api/v1/agencies/{id}/documents',
  GET_DOCUMENTS: '/api/v1/agencies/{id}/documents',
  DELETE_DOCUMENT: '/api/v1/agencies/{id}/documents/{documentId}',
} as const;

/**
 * ZIP Code Lookup Endpoints
 */
export const ZIP_CODE_ENDPOINTS = {
  COUNTIES_ALL: '/api/v1/zip-codes/counties-all',
  CITIES_BY_COUNTY: '/api/v1/zip-codes/cities-by-county',
  DISTINCT: '/api/v1/zip-codes/distinct',
} as const;

/**
 * Support Ticket Endpoints (home health provider)
 */
export const TICKET_ENDPOINTS = {
  INCOMING: '/api/v1/tickets/incoming',
  OUTGOING: '/api/v1/tickets/outgoing',
  GET_BY_ID: (id: string) => `/api/v1/tickets/${id}`,
  CREATE: '/api/v1/tickets',
  ADD_MESSAGE: (id: string) => `/api/v1/tickets/${id}/messages`,
  UPDATE_STATUS: (id: string) => `/api/v1/tickets/${id}/status`,
  ESCALATE: (id: string) => `/api/v1/tickets/${id}/escalate`,
} as const;
